// src/services/queries/standing.ts
//
// React-query hooks para Standings (tabela de classificação por fase).

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryParams, Standing } from '@/model';
import { StandingConverter } from '@/converters/standing';
import { StandingService } from '../instances/standing';
import {
  PostStandingRequest,
  PostStandingsBatchRequest,
  PutStandingRequest,
  PutStandingsOrderRequest,
} from '../request/standing';

/** GET /stages/:id/standings — lista paginada. */
export const useStandings = (stageId: string, params?: QueryParams) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['GET_STANDINGS', stageId, params],
    [stageId, params],
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StandingService.getStandings(stageId, params);
      if (err) throw err;
      return {
        data: StandingConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      } as { data: Standing[]; meta: typeof data.meta };
    },
    enabled: !!stageId,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** GET /standings/:id — detalhe de uma standing. */
export const useStandingById = (uuid: string) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_STANDING_BY_ID', uuid], [uuid]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StandingService.getStandingById(uuid);
      if (err) throw err;
      return StandingConverter.toDomainFromByIdResponse(data);
    },
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** GET /stages/:id/standings/summary — top N (default 6). */
export const useStandingsSummary = (stageId: string, params?: QueryParams) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['GET_STANDINGS_SUMMARY', stageId, params],
    [stageId, params],
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StandingService.getStandingsSummary(stageId, params);
      if (err) throw err;
      return StandingConverter.toDomainFromSummaryResponse(data);
    },
    enabled: !!stageId,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** POST /stages/:id/standings — upsert de uma standing. */
export const usePostStandingMutation = () => {
  return useMutation({
    mutationKey: ['POST_STANDING'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PostStandingRequest;
    }) => {
      const [err, data] = await StandingService.postStanding(id, payload);
      if (err) throw err;
      return StandingConverter.toDomainFromPostResponse(data);
    },
  });
};

/** PUT /standings/:id — atualiza estatísticas. */
export const usePutStandingMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STANDING'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PutStandingRequest;
    }) => {
      const [err, data] = await StandingService.putStanding(id, payload);
      if (err) throw err;
      return StandingConverter.toDomainFromPutResponse(data);
    },
  });
};

/** DELETE /standings/:id — remove. */
export const useDeleteStandingMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_STANDING'],
    mutationFn: async (id: string) => {
      const [err] = await StandingService.deleteStanding(id);
      if (err) throw err;
    },
  });
};

/** POST /stages/:id/standings/batch — upsert de várias standings. */
export const usePostStandingsBatchMutation = () => {
  return useMutation({
    mutationKey: ['POST_STANDINGS_BATCH'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PostStandingsBatchRequest;
    }) => {
      const [err, data] = await StandingService.postStandingsBatch(id, payload);
      if (err) throw err;
      return StandingConverter.toDomainFromPostBatchResponse(data);
    },
  });
};

/** PUT /stages/:id/standings/order — reordena posições. */
export const usePutStandingsOrderMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STANDINGS_ORDER'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PutStandingsOrderRequest;
    }) => {
      const [err, data] = await StandingService.putStandingsOrder(id, payload);
      if (err) throw err;
      return StandingConverter.toDomainFromPutOrderResponse(data);
    },
  });
};
